"use client"

import { useEffect, useRef, useState } from "react"

import { micLevel } from "./use-mic-levels"

export type TranscribeAudio = (
	audio: Blob,
	signal: AbortSignal,
) => Promise<string>
export type AudioInputState =
	| "idle"
	| "requesting"
	| "recording"
	| "transcribing"

interface AudioSession {
	controller: AbortController
	recorder?: MediaRecorder
	stream?: MediaStream
	context?: AudioContext
	timer?: ReturnType<typeof setInterval>
	limit?: ReturnType<typeof setTimeout>
}

export function isAudioInputSupported() {
	return (
		typeof window !== "undefined" &&
		!!navigator.mediaDevices?.getUserMedia &&
		typeof MediaRecorder !== "undefined"
	)
}

/** Record locally, then send only when the user stops. Cancel never submits audio. */
export function useAudioInput({
	transcribe,
	onTranscript,
	onError,
}: {
	transcribe?: TranscribeAudio
	onTranscript: (text: string) => void
	onError: (message: string) => void
}) {
	const [state, setState] = useState<AudioInputState>("idle")
	const [levels, setLevels] = useState<number[]>(Array<number>(100).fill(0))
	const current = useRef({ transcribe, onTranscript, onError })
	current.current = { transcribe, onTranscript, onError }
	const session = useRef<AudioSession | null>(null)

	const release = (active: AudioSession) => {
		clearInterval(active.timer)
		clearTimeout(active.limit)
		active.stream?.getTracks().forEach((track) => track.stop())
		void active.context?.close().catch(() => undefined)
	}
	const cancel = () => {
		const active = session.current
		session.current = null
		if (active) {
			active.controller.abort()
			if (active.recorder?.state === "recording") active.recorder.stop()
			release(active)
		}
		setState("idle")
	}
	const stop = () => {
		const active = session.current
		if (active?.recorder?.state !== "recording") return
		setState("transcribing")
		active.recorder.stop()
		release(active)
	}
	const start = async () => {
		if (session.current || !current.current.transcribe) return
		const active: AudioSession = {
			controller: new AbortController(),
		}
		session.current = active
		setState("requesting")
		setLevels(Array<number>(100).fill(0))
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			active.stream = stream
			if (active.controller.signal.aborted) {
				release(active)
				return
			}
			const mimeType = [
				"audio/webm;codecs=opus",
				"audio/mp4",
				"audio/webm",
			].find((type) => MediaRecorder.isTypeSupported(type))
			if (!mimeType)
				throw new Error("This browser cannot record a supported audio format.")
			const recorder = new MediaRecorder(stream, {
				mimeType,
				audioBitsPerSecond: 64000,
			})
			active.recorder = recorder
			const chunks: Blob[] = []
			let size = 0
			recorder.ondataavailable = (event) => {
				if (active.controller.signal.aborted || !event.data.size) return
				size += event.data.size
				if (size > 4 * 1024 * 1024) {
					cancel()
					current.current.onError(
						"Recording is too large. Please try a shorter message.",
					)
					return
				}
				chunks.push(event.data)
			}
			recorder.onerror = () => {
				cancel()
				current.current.onError("Recording failed. Please try again.")
			}
			recorder.onstop = () => {
				release(active)
				if (active.controller.signal.aborted) return
				setState("transcribing")
				const finish = async () => {
					try {
						if (!size)
							throw new Error("No audio was recorded. Please try again.")
						const text = await current.current.transcribe!(
							new Blob(chunks, { type: mimeType }),
							active.controller.signal,
						)
						if (active.controller.signal.aborted) return
						if (text.trim()) current.current.onTranscript(text.trim())
						else
							current.current.onError(
								"No speech was detected. Please try again.",
							)
					} catch (error) {
						if (!active.controller.signal.aborted)
							current.current.onError(
								error instanceof Error
									? error.message
									: "Transcription failed. Please try again.",
							)
					} finally {
						if (session.current === active) {
							session.current = null
							setState("idle")
						}
					}
				}
				void finish()
			}
			// The waveform uses real microphone levels, not decorative motion.
			try {
				const context = new AudioContext()
				active.context = context
				void context.resume().catch(() => undefined)
				const analyser = context.createAnalyser()
				analyser.fftSize = 256
				context.createMediaStreamSource(stream).connect(analyser)
				const samples = new Uint8Array(analyser.fftSize)
				active.timer = setInterval(() => {
					analyser.getByteTimeDomainData(samples)
					const level = micLevel(samples)
					setLevels((previous) => [...previous.slice(1), level])
				}, 70)
			} catch {
				/* Recording still works if audio analysis is unavailable. */
			}
			recorder.start(250)
			setState("recording")
			active.limit = setTimeout(stop, 120_000)
		} catch (error) {
			if (active.controller.signal.aborted) return
			cancel()
			current.current.onError(
				error instanceof DOMException && error.name === "NotAllowedError"
					? "Microphone access was blocked. Please check your browser permissions."
					: "Could not start recording. Check your microphone and try again.",
			)
		}
	}
	useEffect(
		() => () => {
			const active = session.current
			session.current = null
			if (!active) return
			active.controller.abort()
			if (active.recorder?.state === "recording") active.recorder.stop()
			release(active)
		},
		[],
	)
	return {
		state,
		levels,
		start,
		stop,
		cancel,
		supported: isAudioInputSupported(),
	}
}
